import type { CardDetail, CatalogSource, Prices, ProductRef, Verdict } from "../../api/models";
import { isSports } from "../../lib/catalog";

export type CandidateSource = "production" | "run" | "verified" | "search";

export interface Candidate {
  key: string;
  source: CandidateSource;
  id: string;
  name: string;
  console: string;
  label: string;
  verdict?: Verdict;
  identity: Record<string, unknown> | null;
  /** What productUrl() reads: "sports" or anything else. */
  cardType: string | null;
  prices?: Prices;
}

export const SPORTS = [
  "Baseball",
  "Basketball",
  "Football",
  "Ice Hockey",
  "Soccer",
  "Golf",
  "Tennis",
  "Boxing",
  "Wrestling",
  "Other",
];

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

export function productionCardType(card: CardDetail): string | null {
  return str(card.productionCardInfo.card_type) ?? str(card.productionIdentity?.card_type);
}

export function defaultSource(card: CardDetail): CatalogSource {
  return isSports(card.productionProduct.console, productionCardType(card)) ? "scp" : "pricecharting";
}

export function defaultSport(card: CardDetail): string {
  return card.sport ?? str(card.productionIdentity?.sport) ?? str(card.productionCardInfo.sport) ?? "";
}

function pickOf(products: ProductRef[], pickedId: string, pickedName: string | null): ProductRef {
  const p = products.find((x) => x.id === pickedId);
  return { id: pickedId, name: p?.name ?? pickedName ?? pickedId, console: p?.console ?? "", prices: p?.prices };
}

export function buildCandidates(card: CardDetail): Candidate[] {
  const cardType = productionCardType(card);
  const production = card.productionProduct;
  const out: Candidate[] = [
    {
      key: "production",
      source: "production",
      id: production.id,
      name: production.name,
      console: production.console,
      label: "hobbyscan.com",
      identity: card.productionIdentity,
      cardType,
      prices: production.prices,
    },
  ];
  const seen = new Set([production.id]);
  card.runs.forEach((run, i) => {
    if (!run.pickedId || seen.has(run.pickedId)) return;
    seen.add(run.pickedId);
    const p = pickOf(run.products, run.pickedId, run.pickedName);
    out.push({
      key: `run-${run.runId}`,
      source: "run",
      id: p.id,
      name: p.name,
      console: p.console,
      label: i === 0 ? "Latest run" : `Run #${run.runId}`,
      verdict: run.scored.verdict,
      identity: run.identity,
      cardType,
      prices: p.prices,
    });
  });
  if (card.productId && !seen.has(card.productId)) {
    out.push({
      key: "verified",
      source: "verified",
      id: card.productId,
      name: card.cardFullName ?? card.productId,
      console: "",
      label: "Verified",
      identity: card.identity,
      cardType,
    });
  }
  return out;
}

export function searchCandidate(p: ProductRef, source: CatalogSource): Candidate {
  return {
    key: `search-${p.id}`,
    source: "search",
    id: p.id,
    name: p.name,
    console: p.console,
    label: "Search",
    identity: null,
    cardType: source === "scp" ? "sports" : null,
    prices: p.prices,
  };
}

/** The product id a run row was scored against. */
export function truthIdOf(card: CardDetail, basis: "truth" | "production" | null): string | null {
  if (basis === "truth") return card.productId;
  if (basis === "production") return card.productionProduct.id;
  return null;
}
